/**
 * Time Log Edit Page — hardcoded form.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useUpdate } from '@refinedev/core';
import {
  Button, Card, CardContent, Input, Label, Textarea, Checkbox,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const STATUS_OPTIONS = ['draft', 'submitted', 'approved', 'rejected'];

export function TimeTrackerEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: fetchLoading } = useOne({ resource: 'time_logs', id: id! });
  const { mutate: update, isLoading: updateLoading } = useUpdate();
  const [form, setForm] = React.useState<Record<string, unknown> | null>(null);

  React.useEffect(() => { if (data?.data) setForm({ ...data.data }); }, [data]);
  const set = (f: string, v: unknown) => setForm(p => p ? { ...p, [f]: v } : p);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const { id: _id, org_id, date_created, date_updated, employee_id, ...values } = form;
    update({ resource: 'time_logs', id: id!, values }, { onSuccess: () => navigate(`/time-tracker/${id}`) });
  }

  if (fetchLoading || !form) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/time-tracker/${id}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-semibold">Edit Time Log</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="project_name">Project</Label>
                <Input id="project_name" value={String(form.project_name ?? '')} onChange={e => set('project_name', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="task_name">Task</Label>
                <Input id="task_name" value={String(form.task_name ?? '')} onChange={e => set('task_name', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={String(form.description ?? '')} onChange={e => set('description', e.target.value)} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="hours">Hours *</Label>
                <Input id="hours" type="number" min={0.25} max={24} step={0.25} value={Number(form.hours ?? 0)} onChange={e => set('hours', parseFloat(e.target.value) || 0)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="log_date">Date *</Label>
                <Input id="log_date" type="date" value={String(form.log_date ?? '').slice(0, 10)} onChange={e => set('log_date', e.target.value)} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
                  value={String(form.status ?? 'draft')}
                  onChange={e => set('status', e.target.value)}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={Boolean(form.is_billable)} onCheckedChange={v => set('is_billable', v)} />
              <span className="text-sm">Billable</span>
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(`/time-tracker/${id}`)}>Cancel</Button>
              <Button type="submit" disabled={updateLoading}>{updateLoading ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default TimeTrackerEditPage;
